import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Sparkles, Check, HelpCircle, Building2, Store, TrendingUp, DollarSign, Loader2 } from "lucide-react";

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://www.machinerymarketed.com/app-logo.png" 
            alt="MachineryMarketed Logo" 
            className="h-10 w-10"
          />
          <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <a 
          href="https://www.machinerymarketed.com" 
          className="btn-outline text-sm"
          style={{ borderRadius: "0.5rem" }}
        >
          Back to Home
        </a>
      </div>
    </nav>
  );
};

// Currency Selector Component
const CurrencySelector = ({ selected, onChange }) => {
  const currencies = [
    { code: 'AUD', symbol: '$', flag: '🇦🇺' },
    { code: 'USD', symbol: '$', flag: '🇺🇸' },
    { code: 'CAD', symbol: '$', flag: '🇨🇦' }
  ];

  return (
    <div className="flex items-center justify-center gap-2 bg-white/5 rounded-full p-1.5 inline-flex">
      {currencies.map((currency) => (
        <button
          key={currency.code}
          onClick={() => onChange(currency.code)}
          className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
            selected === currency.code
              ? 'bg-[var(--primary-300)] text-white shadow-lg'
              : 'text-[var(--text-200)] hover:text-white hover:bg-white/10'
          }`}
        >
          <span className="mr-1">{currency.flag}</span>
          {currency.code}
        </button>
      ))}
    </div>
  );
};

// Checkout Button Component
const CheckoutButton = ({ tier, isAnnual, isPopular, isEnterprise, loading, setLoading }) => {
  const navigate = useNavigate();
  
  const handleCheckout = async () => {
    // For enterprise annual or POA, redirect to contact form
    if (isEnterprise && isAnnual) {
      navigate('/#contact');
      return;
    }

    const priceKey = isAnnual ? 'annual' : 'monthly';
    const priceId = STRIPE_PRICE_IDS[tier.rooftops]?.[priceKey];
    
    if (!priceId) {
      navigate('/#contact');
      return;
    }

    setLoading(tier.rooftops);

    try {
      // Call backend to create checkout session
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: priceId,
          metadata: {
            tier: tier.name,
            rooftops: tier.rooftops,
            billing: isAnnual ? 'annual' : 'monthly'
          }
        })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      // Redirect to Stripe Checkout
      if (data.url) {
        window.location.href = data.url;
      } else if (window.Stripe) {
        const stripe = window.Stripe('pk_test_51T2QEcJNpU9ZK0s17bm7cP30WkDOdiYbYRpYaTVOZUafJSwpT9OCg2waJdWHgOKxLpjhAgu6BJrbmtmrEN0B9xgJ00SZSh2t0i');
        await stripe.redirectToCheckout({ sessionId: data.sessionId });
      } else {
        throw new Error('Stripe not loaded');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Unable to start checkout. Please try again or contact support.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <button
      onClick={handleCheckout}
      disabled={loading}
      className={`w-full block text-center py-3 px-6 rounded-lg font-semibold transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed ${
        isPopular ? 'btn-primary' : 'btn-outline'
      }`}
      style={{ borderRadius: "0.5rem" }}
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2">
          <Loader2 size={18} className="animate-spin" />
          Processing...
        </span>
      ) : (
        isEnterprise && isAnnual ? 'Contact Sales' : 'Get Started'
      )}
    </button>
  );
};

// Billing Toggle Component
const BillingToggle = ({ isAnnual, onChange }) => (
  <div className="flex items-center justify-center gap-4">
    <span className={`text-sm font-medium transition-colors ${!isAnnual ? 'text-white' : 'text-[var(--text-300)]'}`}>
      Monthly
    </span>
    <button
      onClick={() => onChange(!isAnnual)}
      className="relative w-14 h-7 rounded-full transition-colors duration-300"
      style={{ background: isAnnual ? 'var(--primary-300)' : 'var(--background-300)' }}
    >
      <div
        className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform duration-300 ${
          isAnnual ? 'translate-x-8' : 'translate-x-1'
        }`}
      />
    </button>
    <span className={`text-sm font-medium transition-colors ${isAnnual ? 'text-white' : 'text-[var(--text-300)]'}`}>
      Annual
    </span>
    {isAnnual && (
      <span className="px-2 py-1 rounded-full text-xs font-bold bg-green-500/20 text-green-400">
        Save ~15%
      </span>
    )}
  </div>
);

// Pricing Tier Card Component
const PricingTierCard = ({ tier, currency, isAnnual, index, loadingTier, setLoadingTier }) => {
  const prices = tier.prices[currency];
  const price = isAnnual ? prices.annual : prices.monthly;
  const isPopular = index === 1; // 3-5 rooftops is most popular
  const isEnterprise = tier.rooftops === '10+';

  return (
    <div 
      className={`card p-6 lg:p-8 relative h-full flex flex-col ${isPopular ? 'ring-2 ring-[var(--primary-300)] scale-105 z-10' : ''}`}
      style={{ background: "var(--background-200)" }}
    >
      {isPopular && (
        <div 
          className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-sm font-bold shadow-lg"
          style={{ background: "var(--primary-300)", color: "white" }}
        >
          Most Popular
        </div>
      )}
      
      {/* Header */}
      <div className="text-center mb-6">
        <div 
          className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4"
          style={{ background: isPopular ? "var(--primary-300)" : "var(--primary-100)" }}
        >
          {isEnterprise ? (
            <Building2 size={28} style={{ color: "white" }} />
          ) : (
            <Store size={28} style={{ color: "white" }} />
          )}
        </div>
        <h3 className="text-2xl font-bold mb-1" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
          {tier.name}
        </h3>
        <p className="text-sm" style={{ color: "var(--text-300)" }}>
          {tier.rooftops} Outlets/Rooftops
        </p>
      </div>

      {/* Price */}
      <div className="text-center mb-6 pb-6 border-b border-white/10">
        {isEnterprise ? (
          <div className="py-2">
            <span className="text-3xl font-bold" style={{ color: "var(--primary-300)" }}>
              POA
            </span>
            <p className="text-xs mt-1" style={{ color: "var(--text-300)" }}>
              Contact us for pricing
            </p>
          </div>
        ) : (
          <div>
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-lg" style={{ color: "var(--text-300)" }}>
                {currency === 'AUD' ? 'A$' : currency === 'USD' ? 'US$' : 'C$'}
              </span>
              <span className="text-4xl font-bold" style={{ color: "var(--text-100)" }}>
                {typeof price === 'number' ? price.toLocaleString() : price}
              </span>
            </div>
            <p className="text-sm mt-1" style={{ color: "var(--text-300)" }}>
              per {isAnnual ? 'year' : 'month'}
            </p>
            {isAnnual && (
              <p className="text-xs mt-2" style={{ color: "var(--accent-100)" }}>
                ~{currency === 'AUD' ? 'A$' : currency === 'USD' ? 'US$' : 'C$'}{Math.round(prices.annual / 12).toLocaleString()}/mo equivalent
              </p>
            )}
          </div>
        )}
      </div>

      {/* Features */}
      <ul className="space-y-3 mb-8 flex-grow">
        {tier.features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-3">
            <Check size={18} style={{ color: "var(--primary-300)", flexShrink: 0, marginTop: "2px" }} />
            <span className="text-sm" style={{ color: "var(--text-200)" }}>{feature}</span>
          </li>
        ))}
      </ul>

      {/* CTA Button */}
      <CheckoutButton
        tier={tier}
        isAnnual={isAnnual}
        isPopular={isPopular}
        isEnterprise={isEnterprise}
        loading={loadingTier === tier.rooftops}
        setLoading={setLoadingTier}
      />
    </div>
  );
};

// Pricing Comparison Table Component
const PricingComparisonTable = ({ currency, isAnnual }) => {
  const tiers = [
    { name: "1-2 Rooftops", prices: { AUD: 3120, USD: 2200, CAD: 3015 }, monthly: { AUD: 315, USD: 220, CAD: 305 } },
    { name: "3-5 Rooftops", prices: { AUD: 5390, USD: 3800, CAD: 5210 }, monthly: { AUD: 540, USD: 380, CAD: 525 } },
    { name: "5-9 Rooftops", prices: { AUD: 6240, USD: 4400, CAD: 6030 }, monthly: { AUD: 625, USD: 440, CAD: 605 } },
    { name: "10+ Rooftops", prices: { AUD: 'POA', USD: 'POA', CAD: 'POA' }, monthly: { AUD: 940, USD: 660, CAD: 910 } },
  ];

  const currencySymbol = currency === 'AUD' ? 'A$' : currency === 'USD' ? 'US$' : 'C$';

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left py-4 px-4 text-sm font-semibold" style={{ color: "var(--text-300)" }}>
              Tier
            </th>
            <th className="text-center py-4 px-4 text-sm font-semibold" style={{ color: "var(--text-300)" }}>
              {isAnnual ? 'Annual Price' : 'Monthly Price'}
            </th>
            {isAnnual && (
              <th className="text-center py-4 px-4 text-sm font-semibold" style={{ color: "var(--text-300)" }}>
                Monthly Equivalent
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {tiers.map((tier, idx) => (
            <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors">
              <td className="py-4 px-4">
                <span className="font-semibold" style={{ color: "var(--text-100)" }}>{tier.name}</span>
              </td>
              <td className="text-center py-4 px-4">
                {tier.prices[currency] === 'POA' ? (
                  <span className="font-bold" style={{ color: "var(--primary-300)" }}>POA</span>
                ) : (
                  <span className="font-bold" style={{ color: "var(--text-100)" }}>
                    {currencySymbol}{isAnnual ? tier.prices[currency].toLocaleString() : tier.monthly[currency].toLocaleString()}
                  </span>
                )}
              </td>
              {isAnnual && (
                <td className="text-center py-4 px-4">
                  {tier.prices[currency] === 'POA' ? (
                    <span style={{ color: "var(--text-300)" }}>-</span>
                  ) : (
                    <span style={{ color: "var(--text-300)" }}>
                      {currencySymbol}{Math.round(tier.prices[currency] / 12).toLocaleString()}/mo
                    </span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Stripe Price IDs mapping
const STRIPE_PRICE_IDS = {
  '1-2': {
    monthly: 'price_1T2QKNJNpU9ZK0s1290JEnMf',
    annual: 'price_1T2QwsJNpU9ZK0s1tcHKmtLY'
  },
  '3-5': {
    monthly: 'price_1T2QuUJNpU9ZK0s12hFWi5Nk',
    annual: 'price_1T2QxrJNpU9ZK0s1LmrqB7wS'
  },
  '5-9': {
    monthly: 'price_1T2QvWJNpU9ZK0s1fnjj2KR2',
    annual: 'price_1T2QyVJNpU9ZK0s1rJycyrRc'
  },
  '10+': {
    monthly: 'price_1T2QzdJNpU9ZK0s1D4419E9K',
    annual: null // POA - contact sales
  }
};

// Main Pricing Page Component
const PricingPage = () => {
  const [currency, setCurrency] = useState('AUD');
  const [isAnnual, setIsAnnual] = useState(false);
  const [loadingTier, setLoadingTier] = useState(null);

  const pricingTiers = [
    {
      name: "Starter",
      rooftops: "1-2",
      prices: {
        AUD: { annual: 3120, monthly: 315 },
        USD: { annual: 2200, monthly: 220 },
        CAD: { annual: 3015, monthly: 305 }
      },
      features: [
        "Up to 2 dealership locations",
        "Unlimited appraisals",
        "All marketplace integrations",
        "Email support",
        "Standard reporting",
        "Mobile app access",
        "Cloud backup"
      ]
    },
    {
      name: "Growth",
      rooftops: "3-5",
      prices: {
        AUD: { annual: 5390, monthly: 540 },
        USD: { annual: 3800, monthly: 380 },
        CAD: { annual: 5210, monthly: 525 }
      },
      features: [
        "Up to 5 dealership locations",
        "Unlimited appraisals & valuations",
        "All marketplace integrations",
        "Priority email & chat support",
        "Advanced analytics dashboard",
        "Custom branding options",
        "Mobile app access",
        "Cloud backup & sync"
      ]
    },
    {
      name: "Professional",
      rooftops: "5-9",
      prices: {
        AUD: { annual: 6240, monthly: 625 },
        USD: { annual: 4400, monthly: 440 },
        CAD: { annual: 6030, monthly: 605 }
      },
      features: [
        "Up to 9 dealership locations",
        "Unlimited appraisals & valuations",
        "All marketplace integrations",
        "Priority phone & email support",
        "Advanced analytics & reporting",
        "Custom branding & white-label",
        "Mobile app access",
        "API access available",
        "Cloud backup & sync"
      ]
    },
    {
      name: "Enterprise",
      rooftops: "10+",
      prices: {
        AUD: { annual: 'POA', monthly: 940 },
        USD: { annual: 'POA', monthly: 660 },
        CAD: { annual: 'POA', monthly: 910 }
      },
      features: [
        "10+ dealership locations",
        "Unlimited everything",
        "All marketplace integrations",
        "Dedicated account manager",
        "Custom integrations",
        "Full API access",
        "White-label solutions",
        "Priority 24/7 support",
        "On-site training available"
      ]
    }
  ];

  return (
    <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
      <Navigation />
      
      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative py-20">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://www.machinerymarketed.com/hero_excavator.jpg" 
              alt="Construction machinery"
              className="w-full h-full object-cover"
              style={{ opacity: 0.2 }}
              loading="eager"
            />
            <div 
              className="absolute inset-0"
              style={{ background: "linear-gradient(to bottom, rgba(0,12,37,0.8) 0%, var(--background-100) 100%)" }}
            />
          </div>
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-6"
                style={{ background: "rgba(15, 95, 255, 0.2)", border: "1px solid rgba(15, 95, 255, 0.3)" }}
              >
                <Sparkles size={18} style={{ color: "var(--primary-300)" }} />
                <span style={{ color: "var(--text-100)" }}>Transparent Pricing</span>
              </div>
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-8"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
              >
                Pricing Based on Your <span className="text-gradient">Dealership Size</span>
              </h1>
              <p className="text-xl max-w-3xl mx-auto" style={{ color: "var(--text-200)" }}>
                Simple, scalable pricing that grows with your business. Choose the plan that fits your number of outlets/rooftops.
              </p>
            </div>
          </div>
        </section>

        {/* Pricing Controls */}
        <section className="py-8">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
              <CurrencySelector selected={currency} onChange={setCurrency} />
              <BillingToggle isAnnual={isAnnual} onChange={setIsAnnual} />
            </div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="py-12">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto items-stretch">
              {pricingTiers.map((tier, index) => (
                <PricingTierCard
                  key={index}
                  tier={tier}
                  currency={currency}
                  isAnnual={isAnnual}
                  index={index}
                  loadingTier={loadingTier}
                  setLoadingTier={setLoadingTier}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Quick Reference Table */}
        <section className="py-16" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                Quick Price Reference
              </h2>
              <div className="card p-6" style={{ background: "var(--background-100)" }}>
                <PricingComparisonTable currency={currency} isAnnual={isAnnual} />
              </div>
              <p className="text-center text-sm mt-4" style={{ color: "var(--text-300)" }}>
                Prices shown in {currency}. All plans include unlimited appraisals and marketplace publishing.
              </p>
            </div>
          </div>
        </section>

        {/* Features Overview */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                What's Included in <span className="text-gradient">Every Plan</span>
              </h2>
              <p className="max-w-2xl mx-auto" style={{ color: "var(--text-200)" }}>
                All tiers come with our core features to help you appraise, value, and market your equipment.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                {
                  icon: TrendingUp,
                  title: "Unlimited Appraisals",
                  desc: "Capture as many trade-ins as you need with our guided mobile and web apps."
                },
                {
                  icon: DollarSign,
                  title: "Valuation Tools",
                  desc: "AI-powered valuation assistance with real-time market data and margin analysis."
                },
                {
                  icon: Building2,
                  title: "Marketplace Publishing",
                  desc: "One-click publishing to 7+ major marketplaces and your social channels."
                }
              ].map((feature, idx) => (
                <div key={idx} className="card p-8 text-center" style={{ background: "var(--background-200)" }}>
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
                    style={{ background: "var(--primary-100)" }}
                  >
                    <feature.icon size={32} style={{ color: "white" }} />
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                    {feature.title}
                  </h3>
                  <p style={{ color: "var(--text-200)" }}>{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                Pricing FAQs
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    q: "What's included in the subscription?",
                    a: "All plans include unlimited appraisals, valuations, and listings. You also get access to all marketplace integrations and our mobile apps for iOS and Android."
                  },
                  {
                    q: "Can I change plans later?",
                    a: "Yes! You can upgrade your plan at any time as your dealership grows. Changes take effect immediately, and we'll prorate any difference."
                  },
                  {
                    q: "Is there a setup fee?",
                    a: "No setup fees for Starter, Growth, and Professional plans. Enterprise plans may have custom implementation fees depending on complexity and training requirements."
                  },
                  {
                    q: "What payment methods do you accept?",
                    a: "We accept all major credit cards, bank transfers, and can arrange invoicing for annual contracts. Enterprise customers can also pay by invoice."
                  },
                  {
                    q: "Can I pay in a different currency?",
                    a: "Yes! We support AUD, USD, and CAD. Prices are fixed in each currency, so there are no surprise exchange rate fluctuations."
                  },
                  {
                    q: "Is there a discount for annual billing?",
                    a: "Yes! Annual billing saves approximately 15% compared to monthly payments. You can switch between annual and monthly at your renewal date."
                  }
                ].map((faq, index) => (
                  <div key={index} className="card p-6" style={{ background: "var(--background-100)" }}>
                    <div className="flex items-start gap-3">
                      <HelpCircle size={20} style={{ color: "var(--primary-300)", flexShrink: 0 }} />
                      <div>
                        <h4 className="font-bold mb-2" style={{ color: "var(--text-100)" }}>{faq.q}</h4>
                        <p style={{ color: "var(--text-200)" }}>{faq.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div 
              className="relative rounded-2xl overflow-hidden max-w-4xl mx-auto"
            >
              <div 
                className="absolute inset-0 z-0"
                style={{ background: "linear-gradient(135deg, var(--primary-100) 0%, var(--primary-300) 100%)" }}
              />
              
              <div className="relative z-10 p-12 text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "white" }}>
                  Need a Custom Quote?
                </h2>
                <p className="mb-8 text-lg" style={{ color: "rgba(255,255,255,0.9)" }}>
                  Have 10+ rooftops or special requirements? Contact us for a personalized quote tailored to your dealership group.
                </p>
                <Link 
                  to="/#contact"
                  className="inline-block px-8 py-4 rounded-lg font-semibold transition-all hover:opacity-90"
                  style={{ background: "white", color: "var(--primary-100)", borderRadius: "0.5rem" }}
                >
                  Contact Sales
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ background: "var(--background-200)", borderTop: "1px solid rgba(176, 176, 176, 0.1)" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://www.machinerymarketed.com/app-logo.png" 
                alt="MachineryMarketed Logo" 
                className="h-10 w-10"
              />
              <span className="font-bold text-lg" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                MACHINERYMARKETED
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Link to="/" className="footer-link text-sm">Home</Link>
              <Link to="/features" className="footer-link text-sm">Features</Link>
              <Link to="/about" className="footer-link text-sm">About</Link>
              <Link to="/faq" className="footer-link text-sm">FAQ</Link>
            </div>

            <p className="text-sm" style={{ color: "var(--text-300)" }}>
              © 2026 MachineryMarketed. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
